import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenText, X } from 'lucide-react';

/**
 * Bible Verse modal — extracted from App.js HomePage (Feb 2026 P3 refactor).
 * Self-contained: takes display state + data as props, owns no state itself.
 */
const BibleVerseModal = ({ open, onClose, verseData, loading }) => (
  <AnimatePresence>
    {open && (
      <div
        data-testid="bible-verse-modal"
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500">
                  <BookOpenText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-green-900">
                    {verseData?.reference || 'Loading...'}
                  </h3>
                  <p className="text-sm text-green-700">King James Version (KJV)</p>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="bible-verse-modal-close"
                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <X className="w-6 h-6 text-slate-600" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-120px)] p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
                <p className="text-slate-600">Loading Bible verse...</p>
              </div>
            ) : verseData ? (
              <div className="space-y-4">
                {verseData.error ? (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                    <p className="text-red-800">{verseData.text}</p>
                  </div>
                ) : (
                  <>
                    <div className="prose prose-lg max-w-none">
                      <p className="text-slate-800 leading-relaxed text-lg whitespace-pre-wrap">
                        {verseData.text}
                      </p>
                    </div>
                    {verseData.verses && verseData.verses.length > 0 && (
                      <div className="mt-6 space-y-3">
                        <h4 className="font-semibold text-slate-700 text-sm">Verses:</h4>
                        {verseData.verses.map((verse) => (
                          <div
                            key={`${verse.book_name}-${verse.chapter}-${verse.verse}`}
                            className="p-3 rounded-xl bg-green-50 border border-green-200"
                          >
                            <p className="text-sm font-medium text-green-800 mb-1">
                              {verse.book_name} {verse.chapter}:{verse.verse}
                            </p>
                            <p className="text-slate-700">{verse.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

export default BibleVerseModal;
